'use client';
import React, { useState } from 'react';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import JsonLd from '@/components/common/JsonLd';

/**
 * FAQSection - A premium accordion-based FAQ component with FAQPage schema.
 * Designed to improve semantic SEO and buyer clarity on service pages.
 */
const FAQSection = ({ items, title = "Frequently Asked Questions", description }) => {
    const [openIndex, setOpenIndex] = useState(0);

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <section className="w-full py-24 md:py-32 bg-white">
            <JsonLd type="FAQPage" data={items} />
            
            <div className="max-w-[1216px] mx-auto px-6 sm:px-8">
                <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
                    
                    {/* Left: Section Header */}
                    <div className="lg:w-1/3 flex flex-col gap-6">
                        <div className="flex items-center gap-2 text-blue-600">
                            <HelpCircle className="w-5 h-5" />
                            <span className="text-sm font-bold tracking-[0.2em] uppercase">Support</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-[#101828] leading-tight">
                            {title}
                        </h2>
                        {description && (
                            <p className="text-slate-600 text-lg leading-relaxed">
                                {description}
                            </p>
                        )}
                        <div className="pt-4">
                            <a 
                                href="/contact" 
                                className="inline-flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 transition-colors"
                            >
                                Still have questions? Contact us
                                <Plus className="w-4 h-4 rotate-45" />
                            </a>
                        </div>
                    </div>

                    {/* Right: Accordion */}
                    <div className="lg:w-2/3 flex flex-col border-t border-slate-100">
                        {items.map((item, idx) => (
                            <div 
                                key={idx} 
                                className="border-b border-slate-100"
                            >
                                <button 
                                    onClick={() => toggleAccordion(idx)}
                                    className="w-full py-6 flex items-center justify-between gap-4 text-left group"
                                    aria-expanded={openIndex === idx}
                                >
                                    <h3 className={`text-lg font-bold transition-colors duration-300 ${openIndex === idx ? 'text-blue-600' : 'text-[#101828] group-hover:text-blue-600'}`}>
                                        {item.question}
                                    </h3>
                                    <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full border transition-all duration-300 ${openIndex === idx ? 'bg-blue-600 border-blue-600 text-white rotate-180' : 'border-slate-200 text-slate-400 group-hover:border-blue-600 group-hover:text-blue-600'}`}>
                                        {openIndex === idx ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                    </div>
                                </button>
                                
                                <div 
                                    className={`overflow-hidden transition-all duration-500 ease-in-out ${openIndex === idx ? 'max-h-[500px] opacity-100 pb-8' : 'max-h-0 opacity-0'}`}
                                >
                                    <div 
                                        className="text-slate-600 leading-relaxed text-base prose prose-slate max-w-none"
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
};

export default FAQSection;
